import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, TableContainer, IconButton,
  Tooltip, Snackbar, Button, Alert
} from '@mui/material';
import ShareIcon from '@mui/icons-material/Share';
import TvIcon from '@mui/icons-material/Tv';
import { useLanguage } from '../contexts/LanguageContext';
import { decodeState, isShareUrlTooLong, encodeFinalTvState } from '../utils/stateManager';
import { FormatIcon } from './FormatIcons';
import ScoreTable from './ScoreTable';
import TopActionBar from './TopActionBar';
// Import the actual function directly instead of the wrapper
import confetti from 'canvas-confetti';

function Results() {
  const { name } = useParams();
  const { translate } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [results, setResults] = useState(null);
  const [playerDataMap, setPlayerDataMap] = useState(null); // { player: { score, playCount } }
  const [winners, setWinners] = useState([]);
  const [decodedState, setDecodedState] = useState(null);
  const displayTournamentName = decodedState?.name || name || translate('tournament.name');
  const [showCopied, setShowCopied] = useState(false);
  const [shareError, setShareError] = useState(false);
  const [tournamentFormat, setTournamentFormat] = useState('americano');
  const encodedState = useMemo(() => new URLSearchParams(location.search).get('state'), [location.search]);
  const [tvEncodedState, setTvEncodedState] = useState(null);
  
  const [confettiTriggered, setConfettiTriggered] = useState(false);

  // Function to fire confetti - wrap in useCallback to avoid recreating on each render
  const fireConfetti = useCallback(() => {
    if (confettiTriggered) return;

    const fire = (particleRatio, opts) => {
      confetti({
        origin: { y: 0.7 },
        particleCount: Math.floor(200 * particleRatio),
        spread: 60,
        ...opts,
      });
    };

    // Start the firework display
    fire(0.25, { spread: 26, startVelocity: 55 });
    fire(0.2, { spread: 60 });
    fire(0.35, { spread: 100, decay: 0.91, scalar: 0.8 });
    fire(0.1, { spread: 120, startVelocity: 25, decay: 0.92, scalar: 1.2 });
    fire(0.1, { spread: 120, startVelocity: 45 });

    // More dramatic bursts with delay
    setTimeout(() => {
      fire(0.5, { spread: 70, origin: { x: 0.2 } });
      fire(0.5, { spread: 70, origin: { x: 0.8 } });
    }, 300);

    setTimeout(() => {
      fire(0.5, { spread: 50, startVelocity: 50, origin: { x: 0.4, y: 0.6 } });
      fire(0.5, { spread: 50, startVelocity: 50, origin: { x: 0.6, y: 0.6 } });
    }, 600);

    setConfettiTriggered(true);
  }, [confettiTriggered]); // Add dependency here

  // Trigger confetti automatically on component mount
  useEffect(() => {
    if (winners.length > 0 && !confettiTriggered) {
      // Use a timer to ensure the component is fully mounted
      const timer = setTimeout(() => {
        fireConfetti();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [winners, confettiTriggered, fireConfetti]); // Add fireConfetti to dependencies

  // Load and process tournament data
  useEffect(() => {
    if (!encodedState) {
      setDecodedState(null);
      setResults(null);
      setWinners([]);
      setPlayerDataMap(null);
      return;
    }

    try {
      // ...existing state processing code...
      const decoded = decodeState(encodedState);
      if (decoded) {
        setDecodedState(decoded);
        setTournamentFormat(decoded.format || 'americano');
        const winsMap = decoded.wins || {};
        const lossesMap = decoded.losses || {};
        const drawsMap = decoded.draws || {};

        // Build a single playerDataMap with score + played rounds + W/L/D (back-compat friendly)
        const nextPlayerData = {};
        if (decoded.playerData) {
          Object.entries(decoded.playerData).forEach(([player, data]) => {
            nextPlayerData[player] = {
              score: data?.score ?? 0,
              playCount: data?.playCount,
              wins: typeof data?.wins === 'number' ? data.wins : (winsMap[player] ?? 0),
              losses: typeof data?.losses === 'number' ? data.losses : (lossesMap[player] ?? 0),
              draws: typeof data?.draws === 'number' ? data.draws : (drawsMap[player] ?? 0)
            };
          });
        } else if (decoded.scores) {
          const playCounts = decoded.playCount || {};
          Object.entries(decoded.scores).forEach(([player, score]) => {
            nextPlayerData[player] = {
              score: score ?? 0,
              playCount: playCounts[player],
              wins: winsMap[player] ?? 0,
              losses: lossesMap[player] ?? 0,
              draws: drawsMap[player] ?? 0
            };
          });
        }
        setPlayerDataMap(Object.keys(nextPlayerData).length ? nextPlayerData : null);

        const getStats = (player) => {
          const data = nextPlayerData[player] || {};
          return {
            score: typeof data.score === 'number' ? data.score : 0,
            wins: typeof data.wins === 'number' ? data.wins : 0,
            draws: typeof data.draws === 'number' ? data.draws : 0,
            losses: typeof data.losses === 'number' ? data.losses : 0
          };
        };

        // Get current player list (excludes completely removed players)
        const currentPlayers = new Set(decoded.players || []);
        
        let sortedPlayers;
        if (decoded.scores) {
          sortedPlayers = Object.entries(decoded.scores)
            .filter(([player]) => currentPlayers.has(player)) // Exclude removed players
            .sort(([pa, aScore], [pb, bScore]) => {
              if (bScore !== aScore) return bScore - aScore;
              const a = getStats(pa);
              const b = getStats(pb);
              if (b.wins !== a.wins) return b.wins - a.wins;
              if (b.draws !== a.draws) return b.draws - a.draws;
              if (a.losses !== b.losses) return a.losses - b.losses;
              return pa.localeCompare(pb);
            });
        } else if (decoded.playerData) {
          sortedPlayers = Object.entries(decoded.playerData)
            .filter(([player]) => currentPlayers.has(player)) // Exclude removed players
            .map(([player, data]) => [player, data?.score ?? 0])
            .sort(([pa, aScore], [pb, bScore]) => {
              if (bScore !== aScore) return bScore - aScore;
              const a = getStats(pa);
              const b = getStats(pb);
              if (b.wins !== a.wins) return b.wins - a.wins;
              if (b.draws !== a.draws) return b.draws - a.draws;
              if (a.losses !== b.losses) return a.losses - b.losses;
              return pa.localeCompare(pb);
            });
        }

        if (sortedPlayers && sortedPlayers.length > 0) {
          const [topPlayer, topScore] = sortedPlayers[0];
          const topStats = getStats(topPlayer);
          const topPlayers = sortedPlayers.filter(([player, score]) => {
            if (score !== topScore) return false;
            const s = getStats(player);
            return s.wins === topStats.wins && s.draws === topStats.draws && s.losses === topStats.losses;
          });
          setResults(sortedPlayers);
          setWinners(topPlayers);
        }
      } else {
        setDecodedState(null);
      }
    } catch (error) {
      console.error('Error decoding state:', error);
      setDecodedState(null);
    }
  }, [encodedState]);

  // Build trimmed TV snapshot to keep URLs small
  useEffect(() => {
    if (!decodedState) {
      setTvEncodedState(null);
      return;
    }

    try {
      const tvState = encodeFinalTvState(decodedState);
      setTvEncodedState(tvState);
    } catch (error) {
      console.warn('Unable to encode trimmed TV state', error);
      setTvEncodedState(null);
    }
  }, [decodedState]);

  // ...existing handlers and render functions...
  const handleShare = () => {
    // Check if URL is too long
    if (encodedState && isShareUrlTooLong(encodedState)) {
      setShareError(true);
      return;
    }
    navigator.clipboard.writeText(window.location.href);
    setShowCopied(true);
  };

  const openTvMode = () => {
    const stateForTv = tvEncodedState || encodedState;
    if (!stateForTv) return;
    const query = new URLSearchParams({ state: stateForTv });
    const tvUrl = `${window.location.origin}/tournament/${encodeURIComponent(name)}/tv?${query}`;
    window.open(tvUrl, '_blank', 'noopener,noreferrer');
  };

  const formatWinners = (winners) => {
    if (!winners?.length) return '';
    if (winners.length === 1) return winners[0][0];
    if (winners.length === 2) 
      return `${winners[0][0]} ${translate('common.and')} ${winners[1][0]}`;
    
    const allButLast = winners.slice(0, -1).map(w => w[0]).join(', ');
    return `${allButLast} ${translate('common.and')} ${winners[winners.length - 1][0]}`;
  };

  const renderPlayerTable = () => {
    if (!results) return null;
    const rows = results.map(([player, score]) => ({
      name: player,
      score,
      wins: playerDataMap?.[player]?.wins ?? 0,
      draws: playerDataMap?.[player]?.draws ?? 0,
      losses: playerDataMap?.[player]?.losses ?? 0,
      playCount: playerDataMap?.[player]?.playCount
    }));

    return (
      <TableContainer component={Paper}>
        <ScoreTable rows={rows} translate={translate} playerStatus={decodedState?.playerStatus} />
      </TableContainer>
    );
  };

  if (!results) {
    return <Typography>{translate('common.loading')}</Typography>;
  }

  return (
    <Box>
      <TopActionBar>
        <IconButton onClick={handleShare} size="large" color="primary" aria-label={translate('common.share')}>
          <ShareIcon />
        </IconButton>
        {encodedState && (
          <IconButton onClick={openTvMode} size="large" color="primary" aria-label={translate('tvMode.openButton')}>
            <TvIcon />
          </IconButton>
        )}
      </TopActionBar>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          <Typography
            variant="subtitle1"
            color="text.secondary"
            sx={{
              fontSize: { xs: '1rem', sm: '1.125rem' },
              fontWeight: 600,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {translate('tournament.finalResults')}
          </Typography>
          <Typography
            variant="h4"
            sx={{
              fontSize: { xs: '1.5rem', sm: '2.125rem' },
              fontWeight: 700,
              color: 'primary.main',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {displayTournamentName}
          </Typography>
          <Tooltip
            title={tournamentFormat === 'mexicano'
              ? translate('playerSetup.formatMexicano')
              : translate('playerSetup.formatAmericano')}
            arrow
          >
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
              <FormatIcon format={tournamentFormat} size={44} />
            </Box>
          </Tooltip>
        </Box>
      </Box>

      {winners.length > 0 && (
        <Paper
          sx={{
            p: { xs: 2, sm: 3 },
            mb: 3,
            bgcolor: 'primary.main',
            color: 'primary.contrastText'
          }}
        >
          <Typography
            variant="h5"
            align="center"
            sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' }, fontWeight: 700 }}
          >
            {winners.length > 1 ? translate('tournament.winners') : translate('tournament.winner')}: {formatWinners(winners)} {translate('tournament.with')} {winners[0][1]} {translate('common.points')}!
          </Typography>
        </Paper>
      )}

      {renderPlayerTable()}

      <Button
        variant="contained"
        fullWidth
        color="success"
        sx={{ mt: 4, py: 1.5, fontSize: '1.1rem' }}
        onClick={() => {
          navigate('/new');
        }}
      >
        {translate('tournament.newTournament')}
      </Button>

      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
        <Box
          component="a"
          href="https://www.buymeacoffee.com/buva"
          target="_blank"
          rel="noopener noreferrer"
          aria-label={translate('common.buyMeCoffee')}
          sx={{ display: 'inline-flex' }}
        >
          <Box
            component="img"
            src="https://cdn.buymeacoffee.com/buttons/v2/default-yellow.png"
            alt={translate('common.buyMeCoffee')}
            loading="lazy"
            sx={{ height: 60, width: 217, maxWidth: '100%' }}
          />
        </Box>
      </Box>

      <Snackbar
        open={showCopied}
        autoHideDuration={2000}
        onClose={() => setShowCopied(false)}
        message={translate('common.linkCopied')}
      />
      <Snackbar
        open={shareError}
        autoHideDuration={5000}
        onClose={() => setShareError(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setShareError(false)}>
          {translate('common.shareFailed')}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Results;
